import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { getUserLocale } from "../utils/getUserLocale";
import getInitialPageForUser from "../../../[locale]/lib/client/utils/getInitialPageForUser";
import { useShallow } from "zustand/react/shallow";
import { useLauncherStore } from "../contexts/LauncherStoreContext";
import { useSessionContext } from "../../../[locale]/lib/client/contexts/SessionContext";
import {
  useActiveConversation,
  useLastVisitedTab,
  useLocale,
} from "../../../[locale]/lib/client/contexts/DeviceContext";

/**
 * Only use this hook at the entry point of the app, eg /${CustomBasePath}/${TenantId}/${MessengerId}/${HostConfig}
 */
export const useRedirectOnInit = (params: {
  tenantId: string;
  messengerId: string;
  hostConfig: string;
}) => {
  const navigate = useNavigate();
  const { config, isOpen, goTo, setGoTo, overrideLocale } = useLauncherStore(
    useShallow((state) => ({
      config: state.config,
      isOpen: state.isOpen,
      goTo: state.goTo,
      setGoTo: state.setGoTo,
      overrideLocale: state.overrideLocale,
    })),
  );
  const session = useSessionContext().session;
  const { setLocale } = useLocale();
  const { lastVisitedTab } = useLastVisitedTab();
  const {
    activeConversations: { recentActiveConversations, activeConversations },
  } = useActiveConversation();

  return useCallback(() => {
    const locale = getUserLocale(config?.settings.generalSettings, overrideLocale);
    setLocale(locale);

    if (isOpen) {
      // This will automatically redirect to the default tab or active message page, which will trigger async authentication and create a session
      navigate(
        getInitialPageForUser({
          tenantId: params.tenantId,
          messengerId: params.messengerId,
          hostConfig: params.hostConfig,
          locale,
          config,
          goTo,
          lastVisitedTab,
          activeConversations,
        }),
      );
      setGoTo(undefined);
    } else if (recentActiveConversations.length || session) {
      // This will stay on the launcher page, which will trigger async authentication and create a session so we can show missed message alerts on the launcher button
      navigate({
        to: "/$tenantId/$messengerId/$hostConfig/$locale/launcherAuth",
        params: {
          tenantId: params.tenantId,
          messengerId: params.messengerId,
          hostConfig: params.hostConfig,
          locale,
        },
      });
    } else {
      // This will stay on the launcher page, but won't trigger any authentication or session
      navigate({
        to: "/$tenantId/$messengerId/$hostConfig/$locale/launcher",
        params: {
          tenantId: params.tenantId,
          messengerId: params.messengerId,
          hostConfig: params.hostConfig,
          locale,
        },
      });
    }
  }, [
    config,
    overrideLocale,
    setLocale,
    isOpen,
    recentActiveConversations.length,
    session,
    navigate,
    params.tenantId,
    params.messengerId,
    params.hostConfig,
    goTo,
    lastVisitedTab,
    activeConversations,
    setGoTo,
  ]);
};
